import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './Login';
import Signup from './Signup';

function App() {
  // State for search inputs
  const [visaType, setVisaType] = useState('h1b');
  const [employer, setEmployer] = useState('');
  const [employerSuggestions, setEmployerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [jobTitle, setJobTitle] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [year, setYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  
  // State for results
  const [isAggregate, setIsAggregate] = useState(false);
  
  const [h1bResults, setH1bResults] = useState([]);
  const [h1bTotalCount, setH1bTotalCount] = useState(0);
  const [h1bCurrentPage, setH1bCurrentPage] = useState(1);
  const [h1bTotalPages, setH1bTotalPages] = useState(0);
  const [h1bStats, setH1bStats] = useState([]);
  
  const [permResults, setPermResults] = useState([]);
  const [permTotalCount, setPermTotalCount] = useState(0);
  const [permCurrentPage, setPermCurrentPage] = useState(1);
  const [permTotalPages, setPermTotalPages] = useState(0);
  const [permStats, setPermStats] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedVisaType, setSelectedVisaType] = useState(null);

  // Fetch available years on component mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch('https://h1b-visa-backend-production.up.railway.app/api/years');
        const data = await response.json();
        setAvailableYears(data.years);
        if (data.years.length > 0) {
          setYear(data.years[0].toString());
        }
      } catch (error) {
        console.error('Error fetching years:', error);
      }
    };
    fetchYears();
    
    // Check for existing login
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Fetch application details
  const fetchApplicationDetails = async (caseNumber, visaTypeParam) => {
    setModalLoading(true);
    setModalOpen(true);
    setSelectedApplication(null);
    
    try {
      const response = await fetch(`https://h1b-visa-backend-production.up.railway.app/api/application/${visaTypeParam}/${caseNumber}`);
      const data = await response.json();
      
      if (data.error) {
        alert('Application not found');
        setModalOpen(false);
      } else {
        setSelectedApplication(data.application);
        setSelectedVisaType(data.visa_type);
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      alert('Error loading application details');
      setModalOpen(false);
    }
    
    setModalLoading(false);
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedApplication(null);
    setSelectedVisaType(null);
  };

  // Search function
  const handleSearch = async (page = 1) => {
    setLoading(true);
    setSearched(true);
    
    const params = new URLSearchParams();
    params.append('visa_type', visaType);
    if (employer) params.append('employer', employer);
    if (jobTitle) params.append('job_title', jobTitle);
    if (city) params.append('city', city);
    if (state) params.append('state', state);
    if (year) params.append('year', year);
    params.append('page', page);
    params.append('per_page', 25);
    
    try {
      const response = await fetch(`https://h1b-visa-backend-production.up.railway.app/api/search?${params}`);
      const data = await response.json();
      
      setIsAggregate(data.is_aggregate || false);
      
      if (data.is_aggregate) {
        setH1bStats(data.h1b_stats || []);
        setPermStats(data.perm_stats || []);
        setH1bResults([]);
        setPermResults([]);
      } else {
        setH1bStats([]);
        setPermStats([]);
        
        if (data.h1b_results) {
          setH1bResults(data.h1b_results);
          setH1bTotalCount(data.h1b_total_count);
          setH1bCurrentPage(data.page);
          setH1bTotalPages(data.h1b_total_pages);
        } else {
          setH1bResults([]);
          setH1bTotalCount(0);
          setH1bTotalPages(0);
        }
        
        if (data.perm_results) {
          setPermResults(data.perm_results);
          setPermTotalCount(data.perm_total_count);
          setPermCurrentPage(data.page);
          setPermTotalPages(data.perm_total_pages);
        } else {
          setPermResults([]);
          setPermTotalCount(0);
          setPermTotalPages(0);
        }
      }
    } catch (error) {
      console.error('Error searching:', error);
      alert('Error searching. Make sure the backend API is running!');
    }
    
    setLoading(false);
  };


  // Autocomplete for employer field
  const fetchEmployerSuggestions = async (query) => {
    if (query.length < 2) {
      setEmployerSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(`https://h1b-visa-backend-production.up.railway.app/api/employers/autocomplete?query=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      setEmployerSuggestions(data.suggestions || []);
      setShowSuggestions(data.suggestions.length > 0);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setEmployerSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Debounce autocomplete requests
  const handleEmployerChange = (value) => {
    setEmployer(value);
    setActiveSuggestionIndex(-1);
    
    // Clear previous timeout
    if (window.employerTimeout) {
      clearTimeout(window.employerTimeout);
    }
    
    // Set new timeout
    window.employerTimeout = setTimeout(() => {
      fetchEmployerSuggestions(value);
    }, 300);
  };

  const selectSuggestion = (suggestion) => {
    setEmployer(suggestion);
    setEmployerSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setShowLogin(false);
  };

  const handleSignup = (userData) => {
    setUser(userData);
    setShowSignup(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleEmployerKeyDown = (e) => {
    if (!showSuggestions || employerSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => 
        prev < employerSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      selectSuggestion(employerSuggestions[activeSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch(1);
  };

  const goToPage = (page) => {
    handleSearch(page);
    window.scrollTo(0, 0);
  };

  // Helper function to format field names
  const formatFieldName = (field) => {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  

  return (
    <div className="App">
      {/* Top Navigation Header */}
      <div className="top-nav">
        <div className="nav-content">
          <h1 className="nav-logo">🔷 H1B & GC Database</h1>
          
          <div className="nav-right">
  {user ? (
    <div className="user-menu">
      {/* {user.subscription_tier === 'premium' && (
        <span className="premium-badge">⭐ Premium</span>
      )} */}
      <span className="user-name">{user.full_name}</span>
      <div className="user-dropdown">
        <button>▾</button>
        <div className="dropdown-menu">
          <div className="dropdown-item">My Account</div>
          {/* {user.subscription_tier === 'free' && (
            <div className="dropdown-item upgrade" onClick={() => setShowPricing(true)}>Upgrade to Premium</div>
          )} */}
          {/* {user.subscription_tier === 'premium' && (
            <div className="dropdown-item">Manage Subscription</div>
          )} */}
          <div className="dropdown-item" onClick={handleLogout}>Logout</div>
        </div>
      </div>
    </div>
  ) : (
    <div className="auth-buttons">
      <button className="btn-signup" onClick={() => setShowSignup(true)}>
        Sign Up
      </button>
      <button className="btn-login" onClick={() => setShowLogin(true)}>
        Login
      </button>
    </div>
  )}
</div>
        </div>
      </div>

      <header className="App-header">
        <h1>H1B & Green Card Database</h1>
        <p>Search 943,095 visa applications from 2022-2025</p>
      </header>

      <div className="search-container">
        <form onSubmit={handleSubmit}>
          <div className="visa-type-selector">
            <label>Visa Type:</label>
            <select value={visaType} onChange={(e) => setVisaType(e.target.value)}>
              <option value="h1b">H1B Only</option>
              <option value="perm">Green Card Only</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div className="search-grid">
            <div className="autocomplete-container">
              <input
                type="text"
                placeholder="Company Name (e.g., Amazon)"
                value={employer}
                onChange={(e) => handleEmployerChange(e.target.value)}
                onKeyDown={handleEmployerKeyDown}
                onFocus={() => employer.length >= 2 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                autoComplete="off"
              />
              
              {showSuggestions && employerSuggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                  {employerSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className={`autocomplete-item ${index === activeSuggestionIndex ? 'active' : ''}`}
                      onClick={() => selectSuggestion(suggestion)}
                      onMouseEnter={() => setActiveSuggestionIndex(index)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <input
              type="text"
              placeholder="Job Title (e.g., Software Engineer)"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
            
            <input
              type="text"
              placeholder="City (e.g., Seattle)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            
            <input
              type="text"
              placeholder="State (e.g., WA)"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength="2"
            />

            <select value={year} onChange={(e) => setYear(e.target.value)}>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
              <option value="all">All Years</option>
            </select>
          </div>
          
          <button type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search Visa Data'}
          </button>
        </form>
      </div>

      {searched && (
        <div className="results-container">
          {isAggregate ? (
            <>
              {(visaType === 'h1b' || visaType === 'both') && h1bStats.length > 0 && (
                <div className="visa-results-section">
                  <div className="results-header">
                    <h2>H1B Statistics by Year</h2>
                    <p>Aggregate data across all years</p>
                  </div>

                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Certified</th>
                          <th>Denied</th>
                          <th>Withdrawn</th>
                          <th>Certified - Withdrawn</th>
                        </tr>
                      </thead>
                      <tbody>
                        {h1bStats.map((stat, index) => (
                          <tr key={index}>
                            <td>{stat.year}</td>
                            <td>{stat.certified.toLocaleString()}</td>
                            <td>{stat.denied.toLocaleString()}</td>
                            <td>{stat.withdrawn.toLocaleString()}</td>
                            <td>{stat.certified_withdrawn.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(visaType === 'perm' || visaType === 'both') && permStats.length > 0 && (
                <div className="visa-results-section">
                  <div className="results-header">
                    <h2>Green Card (PERM) Statistics by Year</h2>
                    <p>Aggregate data across all years</p>
                  </div>

                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Certified</th>
                          <th>Denied</th>
                          <th>Withdrawn</th>
                          <th>Certified - Expired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permStats.map((stat, index) => (
                          <tr key={index}>
                            <td>{stat.year}</td>
                            <td>{stat.certified.toLocaleString()}</td>
                            <td>{stat.denied.toLocaleString()}</td>
                            <td>{stat.withdrawn.toLocaleString()}</td>
                            <td>{stat.certified_expired.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {(visaType === 'h1b' || visaType === 'both') && (
                <div className="visa-results-section">
                  <div className="results-header">
                    <h2>H1B Results</h2>
                    <p>Found {h1bTotalCount.toLocaleString()} applications • Click any row for details</p>
                  </div>

                  {h1bResults.length > 0 ? (
                    <>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Employer Name</th>
                              <th>Status</th>
                              <th>Job Title</th>
                              <th>Salary</th>
                              <th>City</th>
                              <th>State</th>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {h1bResults.map((result, index) => (
                              <tr 
                                key={index}
                                className="clickable-row"
                                onClick={(e) => {
                                  // Don't trigger if clicking the button
                                  if (e.target.tagName !== 'BUTTON') {
                                    fetchApplicationDetails(result.case_number, 'h1b');
                                  }
                                }}
                              >
                                <td>{result.employer_name}</td>
                                <td>
                                  <span className={`status-badge-inline status-${result.case_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {result.case_status}
                                  </span>
                                </td>
                                <td>{result.job_title}</td>
                                <td>
                                  {result.salary 
                                    ? `$${result.salary.toLocaleString()}` 
                                    : 'N/A'}
                                </td>
                                <td>{result.worksite_city}</td>
                                <td>{result.worksite_state}</td>
                                <td>{result.employment_start_date}</td>
                                <td>{result.employment_end_date}</td>
                                <td>
                                  <button
                                    className="view-details-btn"
                                    onClick={() => fetchApplicationDetails(result.case_number, 'h1b')}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {h1bTotalPages > 1 && (
                        <div className="pagination">
                          <button 
                            onClick={() => goToPage(h1bCurrentPage - 1)}
                            disabled={h1bCurrentPage === 1}
                          >
                            Previous
                          </button>
                          
                          <span>
                            Page {h1bCurrentPage} of {h1bTotalPages}
                          </span>
                          
                          <button 
                            onClick={() => goToPage(h1bCurrentPage + 1)}
                            disabled={h1bCurrentPage === h1bTotalPages}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="no-results">No H1B results found.</p>
                  )}
                </div>
              )}

              {(visaType === 'perm' || visaType === 'both') && (
                <div className="visa-results-section">
                  <div className="results-header">
                    <h2>Green Card (PERM) Results</h2>
                    <p>Found {permTotalCount.toLocaleString()} applications • Click any row for details</p>
                  </div>

                  {permResults.length > 0 ? (
                    <>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Employer Name</th>
                              <th>Status</th>
                              <th>Job Title</th>
                              <th>Salary</th>
                              <th>City</th>
                              <th>State</th>
                              <th>Received Date</th>
                              <th>Decision Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {permResults.map((result, index) => (
                              <tr 
                                key={index}
                                className="clickable-row"
                                onClick={(e) => {
                                  if (e.target.tagName !== 'BUTTON') {
                                    fetchApplicationDetails(result.case_number, 'perm');
                                  }
                                }}
                              >
                                <td>{result.employer_name}</td>
                                <td>
                                  <span className={`status-badge-inline status-${result.case_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {result.case_status}
                                  </span>
                                </td>
                                <td>{result.job_title}</td>
                                <td>
                                  {result.salary 
                                    ? `$${result.salary.toLocaleString()}` 
                                    : 'N/A'}
                                </td>
                                <td>{result.worksite_city}</td>
                                <td>{result.worksite_state}</td>
                                <td>{result.received_date}</td>
                                <td>{result.decision_date}</td>
                                <td>
                                  <button
                                    className="view-details-btn"
                                    onClick={() => fetchApplicationDetails(result.case_number, 'perm')}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {permTotalPages > 1 && (
                        <div className="pagination">
                          <button 
                            onClick={() => goToPage(permCurrentPage - 1)}
                            disabled={permCurrentPage === 1}
                          >
                            Previous
                          </button>
                          
                          <span>
                            Page {permCurrentPage} of {permTotalPages}
                          </span>
                          
                          <button 
                            onClick={() => goToPage(permCurrentPage + 1)}
                            disabled={permCurrentPage === permTotalPages}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="no-results">No Green Card results found.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            
            {modalLoading ? (
              <div className="modal-loading">Loading application details...</div>
            ) : selectedApplication ? (
              <div className="modal-body">
                <div className="modal-header">
                  <h2>{selectedVisaType === 'h1b' ? 'H1B Application Details' : 'Green Card (PERM) Application Details'}</h2>
                  <div className="case-number">Case: {selectedApplication.case_number}</div>
                  <div className={`status-badge status-${selectedApplication.case_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                    {selectedApplication.case_status}
                  </div>
                </div>

                <div className="modal-details">
  {Object.entries(selectedApplication)
    .filter(([key]) => key !== 'id')
    .map(([key, value]) => (
      <div key={key} className="detail-row">
        <div className="detail-label">{formatFieldName(key)}</div>
        <div className="detail-value blurred-value">
          ████████████
        </div>
      </div>
    ))}
</div>

                {/* Subscribe Banner for Free Users */}
{/* {(!user || user.subscription_tier === 'free') && (
  <div className="subscribe-banner">
    <h3>⭐ Premium Benefits</h3>
    <div className="subscribe-content">
      <div className="subscribe-benefits">
        <ul>
          <li>✓ View all application details</li>
          <li>✓ Unlimited searches</li>
          <li>✓ Export data (coming soon)</li>
        </ul>
      </div>
      <div className="subscribe-action">
        <p className="pricing">Only $10/month • Cancel anytime</p>
        <button className="subscribe-btn">Upgrade to Premium</button>
      </div>
    </div>
  </div>
)} */}
              </div>
            ) : null}
          </div>
        </div>
      )}
      {/* Auth Modals */}
      {showLogin && (
        <Login
          onLogin={handleLogin}
          onSwitchToSignup={() => {
            setShowLogin(false);
            setShowSignup(true);
          }}
        />
      )}

      {showSignup && (
        <Signup
          onSignup={handleSignup}
          onSwitchToLogin={() => {
            setShowSignup(false);
            setShowLogin(true);
          }}
        />
      )}
      {/* {showPricing && <Pricing onClose={() => setShowPricing(false)} />} */}
    </div>
  );
}

export default App;